import type { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';

const failedResponseSchema = z.object({ status: z.number().min(400) });

const reportedSchema = z.object({ reported: z.literal(true) });

/** A failed audit action that the service has already reported through `setError`. */
export type ReportedAuditFailure<T extends object = object> = T & { readonly reported: true };

/**
 * What an audit request returns when it fails: the HTTP response for the caller to report, or, marked as reported, the response or
 * error the service has already shown.
 */
export type FailedAuditRequest = AxiosResponse | ReportedAuditFailure<AxiosResponse | AxiosError>;

/**
 * Tells whether an audit action such as `reviewEntity` failed.
 * A failed action returns the HTTP response of its request, or the error itself when no response arrived.
 */
export function isAuditFailure(result: unknown) {
   return result instanceof Error || failedResponseSchema.safeParse(result).success;
}

/** Marks a failed audit action as reported. Call it where the service calls `setError` for that failure. */
export function markAuditFailureReported<T extends object>(failure: T): ReportedAuditFailure<T> {
   return Object.assign(failure, { reported: true as const });
}

/**
 * Tells whether the service has already reported a failed audit action, so the caller must not report it again.
 * The caller reports every other failure.
 */
export function isAuditFailureReported(result: unknown): result is ReportedAuditFailure {
   return isAuditFailure(result) && reportedSchema.safeParse(result).success;
}
